import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, Legend, 
  ResponsiveContainer, CartesianGrid, ScatterChart, Scatter, AreaChart, Area, ComposedChart,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, FunnelChart, Funnel,
  Label
} from 'recharts';

const API_URL = '/api';

// Chart colors - same as in DashboardBuilder
const COLORS = ['#033A3E', '#075D5F', '#f1fbfb', '#0f1724', '#6b7280'];

// Function to determine column type - same as in DashboardBuilder
const getColumnType = (columnName) => {
  const name = columnName.toLowerCase();
  
  // Date/Time columns
  if (name.includes('date') || name.includes('time') || name.includes('createdat') || name.includes('updatedat')) {
    return 'date';
  }
  
  // Numeric columns
  if (name.includes('amount') || name.includes('price') || name.includes('quantity') || 
      name.includes('total') || name.includes('rate') || name.includes('value') || 
      name.includes('discount') || name.includes('tax') || name.includes('number') ||
      name.includes('id') && !name.includes('code')) {
    return 'numeric';
  }
  
  // ID/Text fields
  if (name.includes('code') || name.includes('id') || name.includes('number') && !name.includes('order')) {
    return 'id';
  }
  
  // Default to categorical
  return 'categorical';
};

// Function to aggregate data for charts - improved for proper grouping and summation
const aggregateDataForChart = (data, chartType, groupBy, valueField, valueField2 = null) => {
  if (!data || data.length === 0) return [];
  
  console.log(`Aggregating data for ${chartType} chart. Group by: ${groupBy}, Value field: ${valueField}, Value field2: ${valueField2}`);
  console.log('Sample data:', data.slice(0, 2));
  
  // For scatter plot, we don't need aggregation
  if (chartType === 'scatter') {
    return data.map(item => ({
      x: parseFloat(item[groupBy]) || 0,
      y: parseFloat(item[valueField]) || 0,
      name: item.name || item[Object.keys(item).find(k => k !== groupBy && k !== valueField)] || ''
    }));
  }
  
  // Check if the groupBy and valueField exist in the data
  if (data.length > 0 && (!data[0].hasOwnProperty(groupBy) || !data[0].hasOwnProperty(valueField))) {
    console.error(`groupBy field '${groupBy}' or valueField '${valueField}' not found in data`);
    return [];
  }
  
  // Aggregate data for other chart types
  const aggregated = {};
  data.forEach(item => {
    const key = item[groupBy];
    
    // Handle numeric values that might be strings
    let value = 0;
    if (typeof item[valueField] === 'string') {
      // Remove currency symbols and commas, then convert to number
      value = parseFloat(item[valueField].replace(/[^0-9.-]+/g, '')) || 0;
    } else {
      value = parseFloat(item[valueField]) || 0;
    }
    
    let value2 = 0;
    if (valueField2) {
      if (typeof item[valueField2] === 'string') {
        value2 = parseFloat(item[valueField2].replace(/[^0-9.-]+/g, '')) || 0;
      } else {
        value2 = parseFloat(item[valueField2]) || 0;
      }
    }
    
    if (aggregated[key]) {
      aggregated[key].value += value;
      if (valueField2 !== null) {
        aggregated[key].value2 += value2;
      }
    } else {
      aggregated[key] = { value };
      if (valueField2 !== null) {
        aggregated[key].value2 = value2;
      }
    }
  });
  
  // Convert to array format for charts
  const result = Object.keys(aggregated).map(key => ({
    name: key,
    value: aggregated[key].value,
    ...(valueField2 !== null && { value2: aggregated[key].value2 })
  }));
  
  console.log('Aggregated result:', result);
  return result;
};

// Custom active shape for pie chart - same as in DashboardBuilder
const renderActiveShape = (props) => {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  
  return (
    <g>
      <path d={`M${cx},${cy} L${cx + outerRadius * Math.cos(-startAngle * Math.PI / 180)},${cy + outerRadius * Math.sin(-startAngle * Math.PI / 180)} 
        A${outerRadius},${outerRadius} 0 0,1 ${cx + outerRadius * Math.cos(-endAngle * Math.PI / 180)},${cy + outerRadius * Math.sin(-endAngle * Math.PI / 180)} 
        Z`} 
        fill={fill} 
        stroke="#fff" 
        strokeWidth={2}
      />
      <path d={`M${cx},${cy} L${cx + innerRadius * Math.cos(-startAngle * Math.PI / 180)},${cy + innerRadius * Math.sin(-startAngle * Math.PI / 180)} 
        A${innerRadius},${innerRadius} 0 0,0 ${cx + innerRadius * Math.cos(-endAngle * Math.PI / 180)},${cy + innerRadius * Math.sin(-endAngle * Math.PI / 180)} 
        Z`} 
        fill="#fff" 
        stroke={fill} 
        strokeWidth={2}
      />
    </g>
  );
};

// Chart type options
const CHART_TYPE_OPTIONS = [
  { value: 'bar', label: 'Bar Chart' },
  { value: 'line', label: 'Line Chart' },
  { value: 'doubleline', label: 'Double Line Chart' },
  { value: 'dashedline', label: 'Dashed Line Chart' },
  { value: 'area', label: 'Area Chart' },
  { value: 'stackedarea', label: 'Stacked Area Chart' },
  { value: 'cardinalarea', label: 'Cardinal Area Chart' },
  { value: 'stackbar', label: 'Stacked Bar Chart' },
  { value: 'composed', label: 'Composed Chart' },
  { value: 'samedatacomposed', label: 'Same Data Composed Chart' },
  { value: 'pie', label: 'Pie Chart' },
  { value: 'twolevelpie', label: 'Two Level Pie Chart' },
  { value: 'straightanglepie', label: 'Straight Angle Pie Chart' },
  { value: 'customactiveshape', label: 'Custom Active Shape Pie Chart' },
  { value: 'gaproundedcorners', label: 'Pie Chart with Gap Rounded Corners' },
  { value: 'scatter', label: 'Scatter Plot' }
];

const DashboardViewer = () => {
  const { id } = useParams();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [containerHeight, setContainerHeight] = useState('100vh');
  const [chartTypes, setChartTypes] = useState({}); // State to track chart types for each element

  // Update the useEffect that fetches the dashboard
  useEffect(() => {
    const fetchDashboardAndData = async () => {
      try {
        const token = localStorage.getItem('authToken') || '';
        const response = await fetch(`${API_URL}/dashboards/${id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) throw new Error('Failed to fetch dashboard');
        
        const dashboardData = await response.json();
        setDashboard(dashboardData);
        
        // Parse the design JSON
        if (dashboardData.DesignJSON) {
          try {
            const design = JSON.parse(dashboardData.DesignJSON);
            const elements = design.elements || [];
            
            // Initialize chart types state
            const initialChartTypes = {};
            elements.forEach(element => {
              if (element.type !== 'text' && element.type !== 'table') {
                initialChartTypes[element.id] = element.type;
              }
            });
            setChartTypes(initialChartTypes);
            
            // Fetch data for each element
            const elementsWithData = await Promise.all(elements.map(async (element) => {
              // Fetch fresh data for this element
              const apiData = await fetchElementData(element);
              
              // For views, we only have header data
              if (element.sourceType === 'view') {
                return {
                  ...element,
                  data: apiData.header || []
                };
              }
              
              // For screens, combine header and details data if needed
              const tables = element.tables || { header: true, details: false };
              const combinedData = combineHeaderAndDetailsData(
                apiData.header, 
                apiData.details, 
                element.columns, 
                tables
              );
              
              // Apply filters if any
              let filteredData = [...combinedData];
              
              // Apply date range filter if specified
              if (element.dateRange && element.dateRange.from && element.dateRange.to) {
                filteredData = filteredData.filter(item => {
                  const dateColumn = Object.keys(item).find(key => 
                    key.toLowerCase().includes('date') || 
                    key.toLowerCase().includes('createdat') || 
                    key.toLowerCase().includes('updatedat')
                  );
                  
                  if (!dateColumn) return true;
                  
                  const itemDate = new Date(item[dateColumn]);
                  const fromDate = new Date(element.dateRange.from);
                  const toDate = new Date(element.dateRange.to);
                  return itemDate >= fromDate && itemDate <= toDate;
                });
              }
              
              // Apply column filters
              if (element.filters) {
                Object.keys(element.filters).forEach(column => {
                  if (element.filters[column]) {
                    filteredData = filteredData.filter(item => {
                      const value = String(item[column]).toLowerCase();
                      const filterValue = String(element.filters[column]).toLowerCase();
                      return value.includes(filterValue);
                    });
                  }
                });
              }
              
              // Filter by selected columns
              filteredData = filteredData.map(item => {
                const filteredItem = {};
                element.columns.forEach(col => {
                  if (item[col] !== undefined) {
                    filteredItem[col] = item[col];
                  }
                });
                return filteredItem;
              });

              // Ensure numeric values are properly converted
              filteredData = filteredData.map(item => {
                const newItem = { ...item };
                element.columns.forEach(col => {
                  if (getColumnType(col) === 'numeric' && typeof newItem[col] === 'string') {
                    // Remove currency symbols and commas, then convert to number
                    const numericValue = parseFloat(newItem[col].replace(/[^0-9.-]+/g, ''));
                    if (!isNaN(numericValue)) {
                      newItem[col] = numericValue;
                    }
                  }
                });
                return newItem;
              });

              return {
                ...element,
                data: filteredData
              };
            }));
            
            setDashboard({...dashboardData, elements: elementsWithData});
          } catch (e) {
            console.error('Error parsing dashboard design:', e);
            setError('Invalid dashboard design');
          }
        }
      } catch (err) {
        console.error('Error fetching dashboard:', err);
        setError('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardAndData();
  }, [id]);

  // Calculate container height based on elements (same as in DashboardBuilder)
  const calculateContainerHeight = () => {
    if (!dashboard || !dashboard.elements || dashboard.elements.length === 0) {
      return '100vh';
    }
    
    // Find the bottom-most position of any element
    let maxBottom = 0;
    dashboard.elements.forEach(element => {
      const bottom = element.y + element.height;
      if (bottom > maxBottom) {
        maxBottom = bottom;
      }
    });
    
    // Add extra space (100px) below the last element
    const extraSpace = 100;
    const calculatedHeight = maxBottom + extraSpace;
    
    // Get viewport height
    const viewportHeight = window.innerHeight;
    
    // Use the larger of calculated height or viewport height
    return `${Math.max(calculatedHeight, viewportHeight)}px`;
  };

  useEffect(() => {
    if (dashboard && dashboard.elements) {
      setContainerHeight(calculateContainerHeight());
    }
  }, [dashboard]);

  // Add this function to DashboardViewer component
  const fetchElementData = async (element) => {
    try {
      console.log(`Fetching data for element: ${element.id}`);
      const token = localStorage.getItem('authToken') || '';
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };

      let endpoint;
      if (element.sourceType === 'screen') {
        // Use the first endpoint from apiEndpoints
        if (!element.apiEndpoints || element.apiEndpoints.length === 0) {
          throw new Error(`No API endpoint found for screen: ${element.sourceName}`);
        }
        endpoint = element.apiEndpoints[0];
      } else if (element.sourceType === 'view') {
        // For views, use the view data endpoint
        const viewName = element.sourceName.split('.').pop();
        endpoint = `/db-view-data/dbo.${encodeURIComponent(viewName)}`;
        console.log("using viewName " + viewName);
        console.log(`Using view endpoint: ${endpoint}`);
      } else {
        throw new Error('Invalid source type');
      }

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();
      console.log("fetching data for the endpoint " , endpoint);
      console.log("API response data for element:", element.id, data, endpoint);

      // Process the response data
      let headerData = [];
      let detailsData = [];

      if (Array.isArray(data)) {
        if (element.sourceType === 'screen') {
          // Process screen data with potential nested details
          if (data.length > 0) {
            const firstRecord = data[0];
            const nestedArrays = Object.keys(firstRecord).filter(key => 
              Array.isArray(firstRecord[key])
            );

            if (nestedArrays.length > 0) {
              headerData = data;
              nestedArrays.forEach(arrayKey => {
                data.forEach(header => {
                  if (header[arrayKey] && Array.isArray(header[arrayKey])) {
                    const details = header[arrayKey].map(detail => ({
                      ...detail,
                      _parentRef: header[Object.keys(header).find(k => 
                        k.toLowerCase().includes('id') || 
                        k.toLowerCase().includes('code') || 
                        k.toLowerCase().includes('number')
                      )] || 'unknown'
                    }));
                    detailsData = [...detailsData, ...details];
                  }
                });
              });
            } else {
              headerData = data;
            }
          }
        } else {
          // For views, all data is treated as a single table
          headerData = data;
        }
      } else if (data.data && Array.isArray(data.data)) {
        // Handle the new view endpoint format
        headerData = data.data;
        detailsData = [];
      } else if (data.header && data.details) {
        headerData = Array.isArray(data.header) ? data.header : [];
        detailsData = Array.isArray(data.details) ? data.details : [];
      } else if (data.recordset) {
        headerData = Array.isArray(data.recordset) ? data.recordset : [];
      }

      return {
        header: headerData,
        details: detailsData
      };
    } catch (error) {
      console.error('Error fetching data for element:', element.id, error);
      return { header: [], details: [] };
    }
  };

  // Function to combine and aggregate header and details data
  const combineHeaderAndDetailsData = (headerData, detailsData, selectedColumns, tables) => {
    // Ensure headerData and detailsData are arrays
    headerData = Array.isArray(headerData) ? headerData : [];
    detailsData = Array.isArray(detailsData) ? detailsData : [];
    
    // For views, we only have header data
    if (!tables.header && !tables.details) {
      return headerData;
    }

    if (!tables.header || !tables.details) {
      // If only one table type is selected, return the corresponding data
      return tables.header ? headerData : detailsData;
    }

    // If both header and details are selected, we need to combine them
    let headerPrimaryKey = 'id';
    if (headerData.length > 0) {
      headerPrimaryKey = Object.keys(headerData[0]).find(key => 
        key.toLowerCase().includes('id') || 
        key.toLowerCase().includes('code') || 
        key.toLowerCase().includes('number')
      ) || 'id';
    }

    const detailsForeignKey = '_parentRef';

    // Create a map of header records for quick lookup
    const headerMap = {};
    headerData.forEach(header => {
      const key = header[headerPrimaryKey];
      if (key) {
        headerMap[key] = header;
      }
    });

    // Combine each detail record with its corresponding header record
    const combinedData = [];
    detailsData.forEach(detail => {
      const headerKey = detail[detailsForeignKey];
      if (headerKey && headerMap[headerKey]) {
        const header = headerMap[headerKey];
        const combinedRecord = {};
        
        // Add all selected columns from both header and details
        selectedColumns.forEach(col => {
          if (header.hasOwnProperty(col)) {
            combinedRecord[col] = header[col];
          } else if (detail.hasOwnProperty(col)) {
            combinedRecord[col] = detail[col];
          } else {
            combinedRecord[col] = null;
          }
        });
        
        combinedData.push(combinedRecord);
      }
    });

    return combinedData;
  };

  // Handle chart type change
  const handleChartTypeChange = (elementId, newType) => {
    setChartTypes(prev => ({
      ...prev,
      [elementId]: newType
    }));
  };

  // Render element based on its type - adapted from DashboardBuilder
  const renderElement = (element) => {
    const style = {
      border: '1px solid #d1d5db',
      borderRadius: '4px',
      backgroundColor: element.backgroundColor || '#ffffff',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    };

    // Check if element is a chart
    const isChart = element.type !== 'text' && element.type !== 'table';
    const currentChartType = chartTypes[element.id] || element.type;

    // Render title if it exists
    const renderTitle = () => {
      if (!element.title) return null;
      
      const alignmentStyle = {
        textAlign: element.titleAlignment || 'center',
        padding: '8px',
        fontWeight: 'bold',
        fontSize: `${element.titleFontSize || 14}px`,
        color: element.titleColor || '#000000',
        borderBottom: '1px solid #e5e7eb',
        paddingLeft: `${element.titlePadding || 0}px`,
        paddingRight: `${element.titlePadding || 0}px`
      };
      
      return (
        <div style={alignmentStyle}>
          {element.title}
        </div>
      );
    };

    if (element.type === 'text') {
      return (
        <div style={style}>
          {renderTitle()}
          <div
            style={{
              fontSize: `${element.fontSize}px`,
              fontFamily: element.fontFamily,
              color: element.fill,
              padding: '10px',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center'
            }}
          >
            {element.text}
          </div>
        </div>
      );
    }
    
    if (element.type === 'table') {
      return (
        <div style={style}>
          {renderTitle()}
          <div className="overflow-auto h-full">
            {element.data && element.data.length > 0 ? (
              <table className="min-w-full">
                <thead style={{ backgroundColor: element.tableHeaderColor || '#f3f4f6' }}>
                  <tr>
                    {element.columns.map((key, index) => (
                      <th key={index} className="py-2 px-3 border-b text-left capitalize">{key}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {element.data.map((row, rowIndex) => (
                    <tr 
                      key={rowIndex} 
                      style={{ 
                        backgroundColor: rowIndex % 2 === 0 
                          ? (element.tableRowColor || '#ffffff') 
                          : (element.tableAltRowColor || '#f9fafb')
                      }}
                    >
                      {element.columns.map((key, colIndex) => (
                        <td key={colIndex} className="py-2 px-3 border-b">{row[key]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                No data available. Please check your filters or try refreshing the data.
              </div>
            )}
          </div>
        </div>
      );
    }
    
    // Prepare data for charts
    let chartData = [];
    
    if (element.data && element.data.length > 0) {
      console.log('Preparing chart data for element type:', currentChartType);
      console.log('Chart config:', element.chartConfig);
      
      if (element.chartConfig.groupBy && element.chartConfig.valueField) {
        // Pass valueField2 to aggregation function
        chartData = aggregateDataForChart(
          element.data, 
          currentChartType, 
          element.chartConfig.groupBy, 
          element.chartConfig.valueField,
          element.chartConfig.valueField2
        );
      } else {
        // Default to first two columns if no config
        const firstCol = element.columns[0] || 'name';
        const secondCol = element.columns[1] || 'value';
        const thirdCol = element.columns[2] || 'value2';
        chartData = element.data.map(item => ({
          name: item[firstCol],
          value: parseFloat(item[secondCol]) || 0,
          value2: parseFloat(item[thirdCol]) || 0
        }));
      }
    }
    
    console.log('Final chart data:', chartData);
    
    // Get axis labels
    const getXAxisLabel = () => {
      if (currentChartType === 'scatter') {
        return element.chartConfig.groupBy || 'X-Axis';
      }
      return element.chartConfig.groupBy || 'Category';
    };
    
    const getYAxisLabel = () => {
      if (currentChartType === 'scatter') {
        return element.chartConfig.valueField || 'Y-Axis';
      }
      return element.chartConfig.valueField || 'Value';
    };
    
    // Get axis label properties
    const xAxisLabelProps = element.axisLabels?.x || { 
      x: 200, 
      y: 310, 
      rotation: 0, 
      color: '#000', 
      fontSize: 12, 
      fontFamily: 'Arial',
      text: getXAxisLabel()
    };
    const yAxisLabelProps = element.axisLabels?.y || { 
      x: 20, 
      y: 150, 
      rotation: -90, 
      color: '#000', 
      fontSize: 12, 
      fontFamily: 'Arial',
      text: getYAxisLabel()
    };

    console.log('element x-axis label props:', xAxisLabelProps);
    console.log('element y-axis label props:', yAxisLabelProps);
    
    // Debug: Log label properties only once per element
    if (!element._logged) {
      console.log(`Element ${element.id} label props:`, { xAxisLabelProps, yAxisLabelProps });
      element._logged = true;
    }
    
    return (
      <div style={style}>
        {renderTitle()}
        {/* Chart type selector dropdown - positioned in top-right corner */}
        {isChart && (
          <div style={{ 
            position: 'absolute', 
            top: '10px', 
            right: '10px', 
            zIndex: 10 
          }}>
            <select 
              value={currentChartType}
              onChange={(e) => handleChartTypeChange(element.id, e.target.value)}
              style={{ 
                padding: '4px 8px', 
                borderRadius: '4px', 
                border: '1px solid #ccc',
                fontSize: '12px',
                backgroundColor: '#fff',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              {CHART_TYPE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        )}
        <div className="h-full w-full p-2">
          {chartData.length > 0 ? (
            <>
              {/* Bar Chart */}
              {currentChartType === 'bar' && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name">
                      <Label 
                        value={xAxisLabelProps.text} 
                        position="bottom" 
                        offset={0}
                        style={{ 
                          fill: xAxisLabelProps.color, 
                          fontSize: xAxisLabelProps.fontSize,
                          fontFamily: xAxisLabelProps.fontFamily,
                          transform: `rotate(${xAxisLabelProps.rotation}deg)`
                        }}
                      />
                    </XAxis>
                    <YAxis>
                      <Label 
                        value={yAxisLabelProps.text} 
                        position="left" 
                        angle={-90}
                        style={{ 
                          fill: yAxisLabelProps.color, 
                          fontSize: yAxisLabelProps.fontSize,
                          fontFamily: yAxisLabelProps.fontFamily
                        }}
                      />
                    </YAxis>
                    <Tooltip />
                    <Legend 
                    wrapperStyle={{
    left: 20,                   
    bottom: 20,                    
    position: 'absolute'       
  }}/>
                    <Bar dataKey="value" fill={element.fill || COLORS[0]} name={getYAxisLabel()} />
                  </BarChart>
                </ResponsiveContainer>
              )}
              
              {/* Line Chart */}
              {currentChartType === 'line' && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name">
                      <Label 
                        value={xAxisLabelProps.text} 
                        position="bottom" 
                        offset={0}
                        style={{ 
                          fill: xAxisLabelProps.color, 
                          fontSize: xAxisLabelProps.fontSize,
                          fontFamily: xAxisLabelProps.fontFamily,
                          transform: `rotate(${xAxisLabelProps.rotation}deg)`
                        }}
                      />
                    </XAxis>
                    <YAxis>
                      <Label 
                        value={yAxisLabelProps.text} 
                        position="left" 
                        angle={-90}
                        style={{ 
                          fill: yAxisLabelProps.color, 
                          fontSize: yAxisLabelProps.fontSize,
                          fontFamily: yAxisLabelProps.fontFamily
                        }}
                      />
                    </YAxis>
                    <Tooltip />
                    <Legend 
                    wrapperStyle={{
    left: 20,                   
    bottom: 20,                    
    position: 'absolute'       
  }}/>
                    <Line type="monotone" dataKey="value" stroke={element.fill || COLORS[0]} activeDot={{ r: 8 }} name={getYAxisLabel()} />
                  </LineChart>
                </ResponsiveContainer>
              )}
              
              {/* Double Line Chart */}
              {currentChartType === 'doubleline' && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name">
                      <Label 
                        value={xAxisLabelProps.text} 
                        position="bottom" 
                        offset={0}
                        style={{ 
                          fill: xAxisLabelProps.color, 
                          fontSize: xAxisLabelProps.fontSize,
                          fontFamily: xAxisLabelProps.fontFamily,
                          transform: `rotate(${xAxisLabelProps.rotation}deg)`
                        }}
                      />
                    </XAxis>
                    <YAxis>
                      <Label 
                        value={yAxisLabelProps.text} 
                        position="left" 
                        angle={-90}
                        style={{ 
                          fill: yAxisLabelProps.color, 
                          fontSize: yAxisLabelProps.fontSize,
                          fontFamily: yAxisLabelProps.fontFamily
                        }}
                      />
                    </YAxis>
                    <Tooltip />
                    <Legend 
                    wrapperStyle={{
    left: 20,                   
    bottom: 20,                    
    position: 'absolute'       
  }}/>
                    <Line type="monotone" dataKey="value" stroke={element.fill || COLORS[0]} activeDot={{ r: 8 }} name={getYAxisLabel()} />
                    <Line type="monotone" dataKey="value2" stroke={element.secondaryValueColor || COLORS[1]} activeDot={{ r: 8 }} name={element.chartConfig.valueField2 || 'Secondary Value'} />
                  </LineChart>
                </ResponsiveContainer>
              )}
              
              {/* Dashed Line Chart */}
              {currentChartType === 'dashedline' && (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name">
                      <Label 
                        value={xAxisLabelProps.text} 
                        position="bottom" 
                        offset={0}
                        style={{ 
                          fill: xAxisLabelProps.color, 
                          fontSize: xAxisLabelProps.fontSize,
                          fontFamily: xAxisLabelProps.fontFamily,
                          transform: `rotate(${xAxisLabelProps.rotation}deg)`
                        }}
                      />
                    </XAxis>
                    <YAxis>
                      <Label 
                        value={yAxisLabelProps.text} 
                        position="left" 
                        angle={-90}
                        style={{ 
                          fill: yAxisLabelProps.color, 
                          fontSize: yAxisLabelProps.fontSize,
                          fontFamily: yAxisLabelProps.fontFamily
                        }}
                      />
                    </YAxis>
                    <Tooltip />
                    <Legend 
                    wrapperStyle={{
    left: 20,                   
    bottom: 20,                    
    position: 'absolute'       
  }}/>
                    <Line type="monotone" dataKey="value" stroke={element.fill || COLORS[0]} strokeDasharray="5 5" activeDot={{ r: 8 }} name={getYAxisLabel()} />
                  </LineChart>
                </ResponsiveContainer>
              )}
              
              {/* Area Chart */}
              {currentChartType === 'area' && (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name">
                      <Label 
                        value={xAxisLabelProps.text} 
                        position="bottom" 
                        offset={0}
                        style={{ 
                          fill: xAxisLabelProps.color, 
                          fontSize: xAxisLabelProps.fontSize,
                          fontFamily: xAxisLabelProps.fontFamily,
                          transform: `rotate(${xAxisLabelProps.rotation}deg)`
                        }}
                      />
                    </XAxis>
                    <YAxis>
                      <Label 
                        value={yAxisLabelProps.text} 
                        position="left" 
                        angle={-90}
                        style={{ 
                          fill: yAxisLabelProps.color, 
                          fontSize: yAxisLabelProps.fontSize,
                          fontFamily: yAxisLabelProps.fontFamily
                        }}
                      />
                    </YAxis>
                    <Tooltip />
                    <Legend 
                    wrapperStyle={{
    left: 20,                   
    bottom: 20,                    
    position: 'absolute'       
  }}/>
                    <Area type="monotone" dataKey="value" stroke={element.fill || COLORS[0]} fill={`${element.fill || COLORS[0]}40`} name={getYAxisLabel()} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
              
              {/* Stacked Area Chart */}
              {currentChartType === 'stackedarea' && (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name">
                      <Label 
                        value={xAxisLabelProps.text} 
                        position="bottom" 
                        offset={0}
                        style={{ 
                          fill: xAxisLabelProps.color, 
                          fontSize: xAxisLabelProps.fontSize,
                          fontFamily: xAxisLabelProps.fontFamily,
                          transform: `rotate(${xAxisLabelProps.rotation}deg)`
                        }}
                      />
                    </XAxis>
                    <YAxis>
                      <Label 
                        value={yAxisLabelProps.text} 
                        position="left" 
                        angle={-90}
                        style={{ 
                          fill: yAxisLabelProps.color, 
                          fontSize: yAxisLabelProps.fontSize,
                          fontFamily: yAxisLabelProps.fontFamily
                        }}
                      />
                    </YAxis>
                    <Tooltip />
                    <Legend 
                    wrapperStyle={{
    left: 20,                   
    bottom: 20,                    
    position: 'absolute'       
  }}/>
                    <Area type="monotone" dataKey="value" stackId="1" stroke={element.fill || COLORS[0]} fill={`${element.fill || COLORS[0]}40`} name={getYAxisLabel()} />
                    <Area type="monotone" dataKey="value2" stackId="1" stroke={element.secondaryValueColor || COLORS[1]} fill={`${element.secondaryValueColor || COLORS[1]}40`} name={element.chartConfig.valueField2 || 'Secondary Value'} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
              
              {/* Cardinal Area Chart */}
              {currentChartType === 'cardinalarea' && (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name">
                      <Label 
                        value={xAxisLabelProps.text} 
                        position="bottom" 
                        offset={0}
                        style={{ 
                          fill: xAxisLabelProps.color, 
                          fontSize: xAxisLabelProps.fontSize,
                          fontFamily: xAxisLabelProps.fontFamily,
                          transform: `rotate(${xAxisLabelProps.rotation}deg)`
                        }}
                      />
                    </XAxis>
                    <YAxis>
                      <Label 
                        value={yAxisLabelProps.text} 
                        position="left" 
                        angle={-90}
                        style={{ 
                          fill: yAxisLabelProps.color, 
                          fontSize: yAxisLabelProps.fontSize,
                          fontFamily: yAxisLabelProps.fontFamily
                        }}
                      />
                    </YAxis>
                    <Tooltip />
                    <Legend 
                    wrapperStyle={{
    left: 20,                   
    bottom: 20,                    
    position: 'absolute'       
  }}/>
                    <Area type="monotone" dataKey="value" stroke={element.fill || COLORS[0]} fill={`${element.fill || COLORS[0]}40`} name={getYAxisLabel()} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
              
              {/* Stack Bar Chart */}
              {currentChartType === 'stackbar' && (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name">
                      <Label 
                        value={xAxisLabelProps.text} 
                        position="bottom" 
                        offset={0}
                        style={{ 
                          fill: xAxisLabelProps.color, 
                          fontSize: xAxisLabelProps.fontSize,
                          fontFamily: xAxisLabelProps.fontFamily,
                          transform: `rotate(${xAxisLabelProps.rotation}deg)`
                        }}
                      />
                    </XAxis>
                    <YAxis>
                      <Label 
                        value={yAxisLabelProps.text} 
                        position="left" 
                        angle={-90}
                        style={{ 
                          fill: yAxisLabelProps.color, 
                          fontSize: yAxisLabelProps.fontSize,
                          fontFamily: yAxisLabelProps.fontFamily
                        }}
                      />
                    </YAxis>
                    <Tooltip />
                    <Legend 
                    wrapperStyle={{
    left: 20,                   
    bottom: 20,                    
    position: 'absolute'       
  }}/>
                    <Bar dataKey="value" stackId="a" fill={element.fill || COLORS[0]} name={getYAxisLabel()} />
                    <Bar dataKey="value2" stackId="a" fill={element.secondaryValueColor || COLORS[1]} name={element.chartConfig.valueField2 || 'Secondary Value'} />
                  </BarChart>
                </ResponsiveContainer>
              )}
              
              {/* Composed Chart */}
              {currentChartType === 'composed' && (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name">
                      <Label 
                        value={xAxisLabelProps.text} 
                        position="bottom" 
                        offset={0}
                        style={{ 
                          fill: xAxisLabelProps.color, 
                          fontSize: xAxisLabelProps.fontSize,
                          fontFamily: xAxisLabelProps.fontFamily,
                          transform: `rotate(${xAxisLabelProps.rotation}deg)`
                        }}
                      />
                    </XAxis>
                    <YAxis>
                      <Label 
                        value={yAxisLabelProps.text} 
                        position="left" 
                        angle={-90}
                        style={{ 
                          fill: yAxisLabelProps.color, 
                          fontSize: yAxisLabelProps.fontSize,
                          fontFamily: yAxisLabelProps.fontFamily
                        }}
                      />
                    </YAxis>
                    <Tooltip />
                    <Legend 
                    wrapperStyle={{
    left: 20,                   
    bottom: 20,                    
    position: 'absolute'       
  }}/>
                    <Bar dataKey="value" fill={element.fill || COLORS[0]} name={getYAxisLabel()} />
                    <Line type="monotone" dataKey="value2" stroke={element.secondaryValueColor || COLORS[1]} activeDot={{ r: 8 }} name={element.chartConfig.valueField2 || 'Secondary Value'} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
              
              {/* Same Data Composed Chart */}
              {currentChartType === 'samedatacomposed' && (
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name">
                      <Label 
                        value={xAxisLabelProps.text} 
                        position="bottom" 
                        offset={0}
                        style={{ 
                          fill: xAxisLabelProps.color, 
                          fontSize: xAxisLabelProps.fontSize,
                          fontFamily: xAxisLabelProps.fontFamily,
                          transform: `rotate(${xAxisLabelProps.rotation}deg)`
                        }}
                      />
                    </XAxis>
                    <YAxis>
                      <Label 
                        value={yAxisLabelProps.text} 
                        position="left" 
                        angle={-90}
                        style={{ 
                          fill: yAxisLabelProps.color, 
                          fontSize: yAxisLabelProps.fontSize,
                          fontFamily: yAxisLabelProps.fontFamily
                        }}
                      />
                    </YAxis>
                    <Tooltip />
                    <Legend 
                    wrapperStyle={{
    left: 20,                   
    bottom: 20,                    
    position: 'absolute'       
  }}/>
                    <Bar dataKey="value" fill={element.fill || COLORS[0]} name={getYAxisLabel()} />
                    <Line type="monotone" dataKey="value" stroke={element.secondaryValueColor || COLORS[1]} activeDot={{ r: 8 }} name="Trend" />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
              
              {/* Pie Chart */}
              {currentChartType === 'pie' && (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      outerRadius={80}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend 
                    wrapperStyle={{
    left: 20,                   
    bottom: 20,                    
    position: 'absolute'       
  }}/>
                    {/* Custom X-axis label for pie chart */}
                    <text 
                      x="50%" 
                      y="95%" 
                      textAnchor="middle" 
                      fill={xAxisLabelProps.color}
                      fontSize={xAxisLabelProps.fontSize}
                      fontFamily={xAxisLabelProps.fontFamily}
                      transform={`rotate(${xAxisLabelProps.rotation}, 250, 290)`}
                    >
                      {xAxisLabelProps.text}
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              )}
              
              {/* Two Level Pie Chart */}
              {currentChartType === 'twolevelpie' && (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={60}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[(index + 2) % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend 
                    wrapperStyle={{
    left: 20,                   
    bottom: 20,                    
    position: 'absolute'       
  }}/>
                    {/* Custom X-axis label for pie chart */}
                    <text 
                      x="50%" 
                      y="95%" 
                      textAnchor="middle" 
                      fill={xAxisLabelProps.color}
                      fontSize={xAxisLabelProps.fontSize}
                      fontFamily={xAxisLabelProps.fontFamily}
                      transform={`rotate(${xAxisLabelProps.rotation}, 250, 290)`}
                    >
                      {xAxisLabelProps.text}
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              )}
              
              {/* Straight Angle Pie Chart */}
              {currentChartType === 'straightanglepie' && (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      startAngle={180}
                      endAngle={0}
                      innerRadius={60}
                      outerRadius={80}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend 
                    wrapperStyle={{
    left: 20,                   
    bottom: 20,                    
    position: 'absolute'       
  }}/>
                    {/* Custom X-axis label for pie chart */}
                    <text 
                      x="50%" 
                      y="95%" 
                      textAnchor="middle" 
                      fill={xAxisLabelProps.color}
                      fontSize={xAxisLabelProps.fontSize}
                      fontFamily={xAxisLabelProps.fontFamily}
                      transform={`rotate(${xAxisLabelProps.rotation}, 250, 290)`}
                    >
                      {xAxisLabelProps.text}
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              )}
              
              {/* Custom Active Shape Pie Chart */}
              {currentChartType === 'customactiveshape' && (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      activeIndex={0}
                      activeShape={renderActiveShape}
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend 
                    wrapperStyle={{
    left: 20,                   
    bottom: 20,                    
    position: 'absolute'       
  }}/>
                    {/* Custom X-axis label for pie chart */}
                    <text 
                      x="50%" 
                      y="95%" 
                      textAnchor="middle" 
                      fill={xAxisLabelProps.color}
                      fontSize={xAxisLabelProps.fontSize}
                      fontFamily={xAxisLabelProps.fontFamily}
                      transform={`rotate(${xAxisLabelProps.rotation}, 250, 290)`}
                    >
                      {xAxisLabelProps.text}
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              )}
              
              {/* Pie Chart with Gap Rounded Corners */}
              {currentChartType === 'gaproundedcorners' && (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      cornerRadius={10}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend 
                    wrapperStyle={{
    left: 20,                   
    bottom: 20,                    
    position: 'absolute'       
  }}/>
                    {/* Custom X-axis label for pie chart */}
                    <text 
                      x="50%" 
                      y="95%" 
                      textAnchor="middle" 
                      fill={xAxisLabelProps.color}
                      fontSize={xAxisLabelProps.fontSize}
                      fontFamily={xAxisLabelProps.fontFamily}
                      transform={`rotate(${xAxisLabelProps.rotation}, 250, 290)`}
                    >
                      {xAxisLabelProps.text}
                    </text>
                  </PieChart>
                </ResponsiveContainer>
              )}
              
              {/* Scatter Plot */}
              {currentChartType === 'scatter' && (
                <ResponsiveContainer width="100%" height="100%">
                  <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" dataKey="x" name={getXAxisLabel()}>
                      <Label 
                        value={xAxisLabelProps.text} 
                        position="bottom" 
                        offset={0}
                        style={{ 
                          fill: xAxisLabelProps.color, 
                          fontSize: xAxisLabelProps.fontSize,
                          fontFamily: xAxisLabelProps.fontFamily,
                          transform: `rotate(${xAxisLabelProps.rotation}deg)`
                        }}
                      />
                    </XAxis>
                    <YAxis type="number" dataKey="y" name={getYAxisLabel()}>
                      <Label 
                        value={yAxisLabelProps.text} 
                        position="left" 
                        angle={-90}
                        style={{ 
                          fill: yAxisLabelProps.color, 
                          fontSize: yAxisLabelProps.fontSize,
                          fontFamily: yAxisLabelProps.fontFamily
                        }}
                      />
                    </YAxis>
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                    <Legend 
                    wrapperStyle={{
    left: 20,                   
    bottom: 20,                    
    position: 'absolute'       
  }}/>
                    <Scatter name="Data Points" data={chartData} fill={element.fill || COLORS[0]} />
                  </ScatterChart>
                </ResponsiveContainer>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              No data available for chart. Please check your filters or try refreshing the data.
              <div className="text-xs mt-2">
                Debug: Chart config: {JSON.stringify(element.chartConfig)}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-600 mb-2">Error</h2>
          <p className="text-gray-700">{error || 'Dashboard not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-800">{dashboard.DashboardName}</h1>
          <p className="text-gray-600">Created: {new Date(dashboard.CreatedAt).toLocaleDateString()}</p>
        </div>
        
        {/* Canvas container with absolute positioning */}
        <div 
          className="bg-white shadow-lg rounded-lg overflow-auto relative"
          style={{ 
            minHeight: containerHeight,
            height: 'auto'
          }}
        >
          {dashboard.elements && dashboard.elements.length > 0 ? (
            dashboard.elements.map((element) => (
              <div
                key={element.id}
                className="absolute  p-2"
                style={{
                  left: `${element.x}px`,
                  top: `${element.y}px`,
                  width: `${element.width}px`,
                  height: `${element.height}px`,
                  zIndex: 1
                }}
              >
                {renderElement(element)}
              </div>
            ))
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center p-8 bg-white bg-opacity-80 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-2">No elements in this dashboard</h3>
                <p className="text-gray-500">This dashboard doesn't contain any visualizations yet.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardViewer;